import React, { useState } from 'react';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { fireEvent } from '@/lib/analytics';
import { FAQ_DATA, replaceFAQVariables, type FAQItem } from '@/content/faqs';

interface DomainFAQProps {
  domain?: string;
  binPrice?: number;
  leasePerMonth?: number;
  className?: string;
  placement?: 'top' | 'all';
}

export default function DomainFAQ({
  domain,
  binPrice,
  leasePerMonth,
  className = '',
  placement = 'top',
}: DomainFAQProps) {
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());

  // Filter FAQs by placement (top = Top Objections + Leasing)
  const faqs = placement === 'top' ? FAQ_DATA.filter((faq) => faq.placement === 'top') : FAQ_DATA;

  // Calculate dynamic values
  const leasePerDay = leasePerMonth ? (leasePerMonth / 30).toFixed(0) : undefined;
  const floorPct = '50'; // Configure this as needed

  const variables = {
    domain: domain || 'this domain',
    binPrice: binPrice ? `$${binPrice.toLocaleString()}` : undefined,
    leasePerMonth: leasePerMonth ? `$${leasePerMonth}/mo` : undefined,
    leasePerDay: leasePerDay ? `$${leasePerDay}/day` : undefined,
    floorPct,
  };

  const toggleItem = (id: string, question: string) => {
    const newOpenItems = new Set(openItems);
    const isOpening = !openItems.has(id);

    if (isOpening) {
      newOpenItems.add(id);
      fireEvent('faq_expand', {
        faqId: id,
        q: question,
        domain,
        path: window.location.pathname,
      });
    } else {
      newOpenItems.delete(id);
    }

    setOpenItems(newOpenItems);
  };

  const handleCTAClick = (cta: { label: string; to: string; event: string }) => {
    fireEvent(cta.event, {
      domain,
      path: window.location.pathname,
      ctaLabel: cta.label,
    });
  };

  return (
    <section className={`mt-12 ${className}`} aria-labelledby="domain-faq-heading">
      <div className="text-center mb-8">
        <h2 id="domain-faq-heading" className="text-3xl md:text-4xl font-bold text-foreground">
          Questions before you buy
        </h2>
      </div>

      <Card className="apple-card">
        <CardContent className="p-0">
          <div className="divide-y divide-border">
            {faqs.map((faq) => {
              const isOpen = openItems.has(faq.id);
              const processedAnswer = replaceFAQVariables(faq.answer, variables);

              return (
                <div key={faq.id} className="group">
                  <button
                    className="w-full text-left p-6 hover:bg-muted/30 transition-colors focus:outline-none focus:bg-muted/50 focus:ring-2 focus:ring-primary/20 focus:ring-inset"
                    onClick={() => toggleItem(faq.id, faq.question)}
                    aria-expanded={isOpen}
                    aria-controls={`faq-answer-${faq.id}`}
                    style={{ minHeight: '44px' }}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <h3 className="text-base font-semibold text-foreground pr-2">
                        {faq.question}
                      </h3>
                      <div className="flex-shrink-0">
                        {isOpen ? (
                          <ChevronUp className="w-5 h-5 text-muted-foreground transition-transform" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-muted-foreground transition-transform" />
                        )}
                      </div>
                    </div>
                  </button>

                  {isOpen && (
                    <div
                      id={`faq-answer-${faq.id}`}
                      className="px-6 pb-6 space-y-4 animate-in slide-in-from-top-2 duration-200"
                      role="region"
                      aria-labelledby={`faq-question-${faq.id}`}
                    >
                      <p className="text-muted-foreground leading-relaxed">{processedAnswer}</p>

                      {/* Inline CTAs */}
                      {faq.ctas && faq.ctas.length > 0 && (
                        <div className="flex flex-wrap gap-2 pt-2">
                          {faq.ctas.map((cta, idx) => (
                            <Button
                              key={idx}
                              asChild
                              variant="secondary"
                              size="sm"
                              onClick={() => handleCTAClick(cta)}
                            >
                              <Link to={cta.to}>{cta.label}</Link>
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </section>
  );
}
